import { SearchableSelect } from "@/components/ui/searchable-select";
import { ApplyFromUrl } from "@/lib/openWithConfig";
import { ListEmpty } from "@/components/patterns";
import { dateLocale } from "@/lib/dateTime";
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { InfiniteScroll } from "@/components/ui/infinite-scroll";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  Kanban,
  List,
  CheckCircle,
  XCircle,
  ArrowRight,
  DollarSign,
  IndianRupee,
  Calendar,
  Building,
  User,
  Trash2,
  Edit,
  TrendingUp,
  AlertTriangle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Move,
  GripVertical,
} from "lucide-react";
import { formatINR, formatINRCompact } from "@/lib/utils";
import type { DealRecord, PipelineRecord, PipelineStageRecord, MasterLookupRecord } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { StageMoveDialog, type StageMoveRequest } from "@/components/deals/StageMoveDialog";
import { FilterBar } from "@/components/filters/FilterBar";
import { DealQuickView } from "./DealQuickView";
import { DealsForecast } from "./DealsForecast";
import { OwnerCell } from "@/components/inline/OwnerCell";
import { CsvImportDialog, type ImportField } from "@/components/import/CsvImportDialog";
import { MoveSelect } from "@/components/boards/MoveSelect";
import { PreviewButton } from "@/components/preview/PreviewButton";
import { useAiFeature } from "@/lib/ai";
import { HealthDot } from "@/components/ai/HealthDot";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { BulkBar, BulkButton, PageHeader, Person, PersonDot, ViewSwitch, ViewsPanel } from "@/components/patterns";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { InlineCell } from "@/components/inline/InlineCell";
import { usePersistentColumns } from "@/lib/usePersistentColumns";
import { parseCsvToObjects } from "@/lib/csv";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import {
  CalendarClock as FilterCloseIcon,
  CalendarDays as FilterDateIcon,
  Layers3 as FilterStageIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const DEAL_COLUMNS = [
  { key: "deal", label: "Deal", locked: true },
  { key: "stage", label: "Stage" },
  { key: "amount", label: "Amount" },
  { key: "prob", label: "Probability" },
  { key: "close", label: "Close date" },
  { key: "owner", label: "Owner" },
  { key: "last", label: "Last activity" },
  { key: "next", label: "Next step" },
];
const DEAL_COLUMN_KEYS = DEAL_COLUMNS.map((c) => c.key);
const DEAL_FILTER_DEFAULTS = { owner: "all", stage: "all", close: "any", created: "any" };

/** Close-date filter -> API params (overdue / no date are special values) */
const closeParams = (close: string): Record<string, string> =>
  close === "overdue" ? { close: "overdue" } : close === "no_date" ? { close: "none" } : dateRangeParams(close, "close");

/** A column title that sorts the table: first click descending, second ascending, third back to board order */
function SortHead({ label, col, sort, onSort }: { label: string; col: string; sort: string; onSort: (s: string) => void }) {
  const next = sort === `-${col}` ? col : sort === col ? "" : `-${col}`;
  return (
    <button type="button" onClick={() => onSort(next)} className="inline-flex items-center gap-1 hover:text-foreground" aria-label={`Sort by ${label.toLowerCase()}`}>
      {label}
      <span aria-hidden="true" className="text-[10px]">{sort === `-${col}` ? "↓" : sort === col ? "↑" : ""}</span>
    </button>
  );
}

/** Won and lost columns only belong to the views that look at won or lost deals */
const stagesForView = <T extends { is_won_stage?: boolean; is_lost_stage?: boolean }>(view: string, all: T[]) =>
  all.filter((st) => (view === "won_quarter" ? st.is_won_stage : view === "lost" ? st.is_lost_stage : !st.is_won_stage && !st.is_lost_stage));

const money = (v: string) => (Number.isNaN(Number(v.replace(/[,₹\s]/g, ""))) || Number(v.replace(/[,₹\s]/g, "")) < 0 ? "Amount must be a number" : null);
const DEAL_IMPORT_FIELDS: ImportField[] = [
  { key: "name", label: "Deal name", required: true, aliases: ["deal", "title"] },
  { key: "company_name", label: "Company", required: true, aliases: ["company", "account", "account_name", "customer"] },
  { key: "amount", label: "Amount", aliases: ["value", "deal_amount"], check: money },
  { key: "expected_close_date", label: "Expected close date", aliases: ["close_date", "closing_date", "expected_close"], check: (v) => (Number.isNaN(new Date(v).getTime()) ? "Close date isn't a date (use YYYY-MM-DD)" : null) },
  { key: "contact_name", label: "Contact name", aliases: ["contact", "person"] },
  { key: "contact_phone", label: "Contact phone", aliases: ["phone", "mobile"] },
  { key: "contact_email", label: "Contact email", aliases: ["email"], check: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Email isn't valid") },
];

const DEAL_BULK_FIELDS: BulkField[] = [
  { field: "expected_close_date", label: "Expected close", kind: "date", nullable: true },
  { field: "amount", label: "Amount", kind: "number" },
];

export function DealsPage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [pipeline, setPipeline] = useState<PipelineRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"kanban" | "list" | "forecast">("kanban");
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listPageSize, setListPageSize] = useState(30);
  const [stageMeta, setStageMeta] = useState<Record<string, { page: number; total: number; pageSize: number; loading: boolean }>>({});
  const [accountLabel, setAccountLabel] = useState("");
  // An existing contact the new deal is for (when started from a contact or account page)
  const [presetContact, setPresetContact] = useState<{ id: string; name: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  // Panel view (open, mine, closing this month, stale, won this quarter, lost) and the pipeline shown
  const columns = usePersistentColumns("deals", DEAL_COLUMN_KEYS);
  const colHidden = (k: string) => (columns.has(k) ? "" : "hidden");
  // Opened from the AI morning note: /deals?view=at_risk
  const [view, setView] = useState<string>(() => (searchParams.get("view") === "at_risk" ? "at_risk" : "open"));
  const healthOn = useAiFeature("DEAL_HEALTH");
  // Table sort: "" is board order; otherwise amount, -amount, close or -close
  const { sort, setSort } = useSort("deals");
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: number; errors: { row: number; reason: string }[] } | null>(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", due: "", type: "CALL" });
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  const [pipelines, setPipelines] = useState<{ id: string; name: string; is_default?: boolean }[]>([]);
  const [pipelineId, setPipelineId] = useState<string>("");
  // Table selection and bulk actions
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkOwnerOpen, setBulkOwnerOpen] = useState(false);
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [bulkOwnerId, setBulkOwnerId] = useState("");
  const [bulkStageId, setBulkStageId] = useState("");
  const [assignable, setAssignable] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const { user, hasPermission } = useAuth();
  const dealFilters = usePersistentFilters("deals", DEAL_FILTER_DEFAULTS);
  // "+ Add filter" on any deal field the API offers
  const fieldFilters = useFieldFilters("deals");
  const [dealFields, setDealFields] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/Deal/FilterFields").then((res) => setDealFields(unwrapArray<FilterFieldDef>(res))).catch(() => setDealFields([]));
  }, []);
  const [dealOptions, setDealOptions] = useState<{ total: number; owners: OwnerFacet[] }>({ total: 0, owners: [] });
  const filtersReady = React.useRef(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [quickDeal, setQuickDeal] = useState<DealRecord | null>(null);

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [moveDeal, setMoveDeal] = useState<DealRecord | null>(null);
  // Every stage change goes through the gate check (and its dialog when something is missing)
  const [moveRequest, setMoveRequest] = useState<StageMoveRequest>(null);
  const [wonGate, setWonGate] = useState<{ allowed: boolean; missing: string[]; can_override: boolean } | null>(null);
  const [wonReason, setWonReason] = useState("");
  const [wonDeal, setWonDeal] = useState<DealRecord | null>(null);
  const [lostDeal, setLostDeal] = useState<DealRecord | null>(null);
  const [deleteDeal, setDeleteDeal] = useState<DealRecord | null>(null);

  // Kanban Drag & Drop and Scroll Controls
  const kanbanRef = useRef<HTMLDivElement>(null);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [dropSlot, setDropSlot] = useState<{ stageId: string; index: number } | null>(null);

  const autoScrollSpeedRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  const scrollKanban = (direction: "left" | "right") => {
    if (kanbanRef.current) {
      const scrollAmount = direction === "left" ? -380 : 380;
      kanbanRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Continuous animation frame loop for butter-smooth edge scrolling
  useEffect(() => {
    let animId: number;
    const loop = () => {
      if (isDraggingRef.current && autoScrollSpeedRef.current !== 0 && kanbanRef.current) {
        kanbanRef.current.scrollBy({ left: autoScrollSpeedRef.current, behavior: "auto" });
      }
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, []);

  // Track global dragover to detect when dragged card approaches Kanban container edges
  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent) => {
      if (!isDraggingRef.current || !kanbanRef.current) return;
      e.preventDefault();
      const rect = kanbanRef.current.getBoundingClientRect();

      // Only trigger auto-scroll if cursor is in or near the vertical bounds of the Kanban board
      if (e.clientY < rect.top - 60 || e.clientY > rect.bottom + 60) {
        autoScrollSpeedRef.current = 0;
        return;
      }

      const EDGE_ZONE = 150; // Detection zone width in px from container boundary
      const MAX_SPEED = 24;  // Max px per animation frame
      const MIN_SPEED = 5;   // Min starting speed

      if (e.clientX >= rect.right - EDGE_ZONE && e.clientX <= rect.right + 60) {
        // Approaching right edge -> Slide Right
        const distanceIntoZone = Math.max(0, e.clientX - (rect.right - EDGE_ZONE));
        const ratio = Math.min(1, distanceIntoZone / EDGE_ZONE);
        autoScrollSpeedRef.current = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio;
      } else if (e.clientX <= rect.left + EDGE_ZONE && e.clientX >= rect.left - 60) {
        // Approaching left edge -> Slide Left
        const distanceIntoZone = Math.max(0, (rect.left + EDGE_ZONE) - e.clientX);
        const ratio = Math.min(1, distanceIntoZone / EDGE_ZONE);
        autoScrollSpeedRef.current = -(MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio);
      } else {
        // In the neutral middle zone
        autoScrollSpeedRef.current = 0;
      }
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      autoScrollSpeedRef.current = 0;
      setDropSlot(null);
      setDraggedDealId(null);
      setDragOverStageId(null);
    };

    window.addEventListener("dragover", handleGlobalDragOver);
    window.addEventListener("dragend", handleDragEnd);
    window.addEventListener("drop", handleDragEnd);
    return () => {
      window.removeEventListener("dragover", handleGlobalDragOver);
      window.removeEventListener("dragend", handleDragEnd);
      window.removeEventListener("drop", handleDragEnd);
    };
  }, []);

  const handleDropAtSlot = async (
    dealId: string,
    targetStage: PipelineStageRecord,
    targetIndex: number
  ) => {
    isDraggingRef.current = false;
    autoScrollSpeedRef.current = 0;
    setDropSlot(null);
    setDraggedDealId(null);
    setDragOverStageId(null);

    const sourceDeal = deals.find((d) => d.id === dealId);
    if (!sourceDeal) return;

    if (sourceDeal.stage_id !== targetStage.id) {
      if (targetStage.is_won_stage) {
        openWon(sourceDeal);
        return;
      }
      if (targetStage.is_lost_stage) {
        openLost(sourceDeal);
        return;
      }
      setMoveRequest({ dealId: sourceDeal.id, dealName: sourceDeal.name, stage: { id: targetStage.id, name: targetStage.name } });
      return;
    }

    // While the cards are sorted their order isn't theirs to change; moving between stages above still works
    if (sort) {
      info("The cards are sorted", "Choose Manual in Sort by to arrange them by hand.");
      return;
    }

    const otherDeals = deals.filter((d) => d.id !== dealId);
    const stageDeals = otherDeals.filter((d) => d.stage_id === targetStage.id);
    const nonTargetDeals = otherDeals.filter((d) => d.stage_id !== targetStage.id);

    const safeIdx = Math.max(0, Math.min(stageDeals.length, targetIndex));
    const updatedSource = {
      ...sourceDeal,
      stage_id: targetStage.id,
      stage: targetStage,
      probability: targetStage.probability_percent,
      weighted_amount: Number(
        ((Number(sourceDeal.amount || 0) * targetStage.probability_percent) / 100).toFixed(2)
      ),
    };
    stageDeals.splice(safeIdx, 0, updatedSource);

    // Instant state update with new priority order
    setDeals([...nonTargetDeals, ...stageDeals]);

    try {
      const orderedIds = stageDeals.map((d) => d.id);
      await api.patch("/api/Deal/Reorder", { deal_ids: orderedIds });
    } catch (err: any) {
      console.error("Reorder error:", err);
    }
  };

  // Forms
  const [accountMode, setAccountMode] = useState<"existing" | "new">("existing");
  const [form, setForm] = useState({
    name: "",
    account_id: "",
    company_name: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    stage_id: "",
    amount: 1000,
    expected_close_date: "",
  });

  const [moveStageId, setMoveStageId] = useState("");
  const [wonForm, setWonForm] = useState({ won_amount: 0, actual_close_date: "" });
  const [lostForm, setLostForm] = useState({ lost_reason: "", lost_notes: "" });

  const [lostReasons, setLostReasons] = useState<MasterLookupRecord[]>([]);

  const { success, error, info } = useToast();
  const canEditInline = hasPermission("deal.update");

  // Filtering happens in the API (role scope first, then these filters)
  const dealQuery = (extra: { stage_id?: string; page?: number; page_size?: number } = {}) => {
    const f = dealFilters.values;
    return toQuery({
      view,
      // Each stage column is sorted the same way on the board (empty = the cards' own, hand-arranged order)
      sort: sort || undefined,
      pipeline_id: pipelineId || undefined,
      search: search.trim(),
      owner: f.owner,
      stage_id: extra.stage_id !== undefined ? extra.stage_id : f.stage,
      ...closeParams(f.close),
      ...dateRangeParams(f.created, "created"),
      f: fieldFilters.param(dealFields),
      page: extra.page,
      page_size: extra.page_size ?? 30,
    });
  };

  const fillList = async (page: number) => {
    const paged = unwrapPage<DealRecord>(await api.get(`/api/Deal/List${dealQuery({ page })}`));
    setDeals(paged.rows);
    setListTotal(paged.total);
    setListPageSize(paged.page_size);
    setListPage(paged.page);
  };

  const fillKanban = async (stages: { id: string }[]) => {
    const stageFilter = dealFilters.values.stage;
    const columns = stageFilter && stageFilter !== "all" ? stages.filter((s) => s.id === stageFilter) : stages;
    const results = await Promise.all(
      columns.map(async (stg) => {
        const paged = unwrapPage<DealRecord>(await api.get(`/api/Deal/List${dealQuery({ stage_id: stg.id, page: 1 })}`));
        return [stg.id, paged] as const;
      }),
    );
    setDeals(results.flatMap(([, paged]) => paged.rows));
    setStageMeta(
      Object.fromEntries(
        results.map(([id, paged]) => [id, { page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false }]),
      ),
    );
  };

  const loadMoreStage = async (stageId: string) => {
    const meta = stageMeta[stageId];
    if (!meta || meta.loading || meta.page * meta.pageSize >= meta.total) return;
    setStageMeta((current) => ({ ...current, [stageId]: { ...meta, loading: true } }));
    try {
      const paged = unwrapPage<DealRecord>(await api.get(`/api/Deal/List${dealQuery({ stage_id: stageId, page: meta.page + 1 })}`));
      setDeals((prev) => {
        const ids = new Set(prev.map((d) => d.id));
        return [...prev, ...paged.rows.filter((row) => !ids.has(row.id))];
      });
      setStageMeta((current) => ({
        ...current,
        [stageId]: { page: paged.page, total: paged.total, pageSize: paged.page_size, loading: false },
      }));
    } catch (err: any) {
      setStageMeta((current) => ({ ...current, [stageId]: { ...meta, loading: false } }));
      error("Fetch Error", err.message || "Failed to load more deals");
    }
  };

  const loadDealOptions = () =>
    api
      .get("/api/Deal/FilterOptions")
      .then((res) => setDealOptions(unwrapObject(res)))
      .catch(() => undefined);

  const loadViewCounts = () =>
    api
      .get(`/api/Deal/ViewCounts${toQuery({ pipeline_id: pipelineId || undefined })}`)
      .then((res) => setViewCounts(unwrapObject(res)))
      .catch(() => undefined);

  /** Reloads only the deals (used when a filter changes) */
  const loadDeals = async (page = viewMode === "list" ? listPage : 1) => {
    setLoading(true);
    try {
      if (viewMode === "list") {
        await fillList(page);
      } else if (viewMode === "kanban" && pipeline?.stages) {
        await fillKanban(stagesForView(view, pipeline.stages));
      }
      loadViewCounts();
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load deals");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [pipeRes, lostRes, listRes] = await Promise.all([
        api.get(pipelineId ? `/api/Pipeline/ById/${pipelineId}` : "/api/Pipeline/Default"),
        api.get("/api/MasterLookup/ByCategory/DEAL_LOST_REASON"),
        api.get("/api/Pipeline/List").catch(() => null),
      ]);
      loadDealOptions();
      loadViewCounts();
      if (listRes) setPipelines(unwrapArray<any>(listRes));

      const pipeData = unwrapObject<PipelineRecord>(pipeRes);
      setPipeline(pipeData);
      const stages = pipeData?.stages || [];
      if (viewMode === "list") await fillList(1);
      else if (viewMode === "kanban") await fillKanban(stagesForView(view, stages));
      setLostReasons(unwrapArray<MasterLookupRecord>(lostRes));
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load deals or pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reload deals when a filter, the status or the search changes (search waits until typing pauses)
  useEffect(() => {
    if (!filtersReady.current) {
      filtersReady.current = true;
      return;
    }
    const t = setTimeout(() => {
      if (viewMode === "list" && listPage !== 1) setListPage(1);
      else loadDeals(1);
    }, search.trim() ? 350 : 0);
    return () => clearTimeout(t);
  }, [dealFilters.values, fieldFilters.values, dealFields, view, search, viewMode, sort]);

  // Another pipeline: load its stages and deals
  const firstPipelineRun = React.useRef(true);
  useEffect(() => {
    if (firstPipelineRun.current) {
      firstPipelineRun.current = false;
      return;
    }
    loadData();
  }, [pipelineId]);

  // A different set of deals: clear the table selection
  useEffect(() => setSelected(new Set()), [view, pipelineId, search, dealFilters.values, fieldFilters.values, listPage, viewMode]);

  useEffect(() => {
    if (!filtersReady.current || viewMode !== "list") return;
    const t = setTimeout(() => loadDeals(listPage), 0);
    return () => clearTimeout(t);
  }, [listPage]);

  const openAdd = (preset?: { accountId?: string; accountName?: string; contactId?: string; contactName?: string }) => {
    if (!pipeline?.stages?.length) {
      error("Error", "No pipeline stages found");
      return;
    }
    const defaultStage = pipeline.stages.find((s) => !s.is_won_stage && !s.is_lost_stage) || pipeline.stages[0];
    setAccountMode("existing");
    setAccountLabel(preset?.accountName || "");
    setPresetContact(preset?.contactId ? { id: preset.contactId, name: preset.contactName || "Selected contact" } : null);
    setForm({
      name: "",
      account_id: preset?.accountId || "",
      company_name: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      stage_id: defaultStage?.id || "",
      amount: 1000,
      expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });
    setAddOpen(true);
  };

  // "New deal" from an account or contact page: /deals?create=true&account_id=…&contact_id=…
  useEffect(() => {
    if (searchParams.get("create") !== "true" || !pipeline?.stages?.length) return;
    openAdd({
      accountId: searchParams.get("account_id") || undefined,
      accountName: searchParams.get("account_name") || undefined,
      contactId: searchParams.get("contact_id") || undefined,
      contactName: searchParams.get("contact_name") || undefined,
    });
    setSearchParams({}, { replace: true });
  }, [searchParams, pipeline]);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pipeline) return;
    if (accountMode === "new" && !form.company_name.trim()) {
      error("Validation", "Company / Account name is required");
      return;
    }
    if (accountMode === "existing" && !form.account_id) {
      error("Validation", "Please select a customer account or enter a new company name");
      return;
    }
    setActionLoading(true);
    try {
      await api.post("/api/Deal/Insert", {
        name: form.name.trim(),
        account_id: accountMode === "existing" ? form.account_id : undefined,
        company_name: accountMode === "new" ? form.company_name.trim() : undefined,
        primary_contact_id: presetContact && accountMode === "existing" ? presetContact.id : undefined,
        contact_name: presetContact ? undefined : form.contact_name?.trim() || undefined,
        contact_phone: presetContact ? undefined : form.contact_phone?.trim() || undefined,
        contact_email: presetContact ? undefined : form.contact_email?.trim() || undefined,
        pipeline_id: pipeline.id,
        stage_id: form.stage_id,
        amount: Number(form.amount),
        expected_close_date: form.expected_close_date ? new Date(form.expected_close_date) : undefined,
      });
      success("Opportunity Added", "Successfully created and placed on the Sales Pipeline");
      setAddOpen(false);
      loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create deal");
    } finally {
      setActionLoading(false);
    }
  };

  const openMove = (d: DealRecord) => {
    setMoveDeal(d);
    setMoveStageId(d.stage_id);
  };

  const handleMoveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moveDeal) return;
    const target = stages.find((s) => s.id === moveStageId);
    if (!target || target.id === moveDeal.stage_id) {
      setMoveDeal(null);
      return;
    }
    if (target.is_won_stage) {
      const d = moveDeal;
      setMoveDeal(null);
      openWon(d);
      return;
    }
    if (target.is_lost_stage) {
      const d = moveDeal;
      setMoveDeal(null);
      openLost(d);
      return;
    }
    setMoveRequest({ dealId: moveDeal.id, dealName: moveDeal.name, stage: { id: target.id, name: target.name } });
    setMoveDeal(null);
  };

  const openWon = (d: DealRecord) => {
    setWonDeal(d);
    setWonGate(null);
    setWonReason("");
    setWonForm({
      won_amount: Number(d.amount),
      actual_close_date: new Date().toISOString().slice(0, 10),
    });
    const wonStage = stages.find((s) => s.is_won_stage);
    if (wonStage) {
      api
        .get(`/api/Deal/Gates/${d.id}?target=${wonStage.id}`)
        .then((res: any) => setWonGate({ allowed: !!res?.move?.allowed, missing: res?.move?.missing || [], can_override: !!res?.can_override }))
        .catch(() => undefined);
    }
  };

  const handleWonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wonDeal) return;
    setActionLoading(true);
    try {
      await api.patch(`/api/Deal/MarkWon/${wonDeal.id}`, {
        won_amount: Number(wonForm.won_amount),
        actual_close_date: new Date(wonForm.actual_close_date),
        ...(wonGate && !wonGate.allowed ? { override: true, reason: wonReason.trim() } : {}),
      });
      success("Deal Closed as Won", "Deal successfully closed as Won.");
      setWonDeal(null);
      loadData();
    } catch (err: any) {
      error("Failed", err.message || "Could not close deal");
    } finally {
      setActionLoading(false);
    }
  };

  const openLost = (d: DealRecord) => {
    setLostDeal(d);
    setLostForm({ lost_reason: "", lost_notes: "" });
  };

  const handleLostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lostDeal) return;
    if (!lostForm.lost_reason.trim()) {
      error("Validation", "Lost reason is mandatory");
      return;
    }
    setActionLoading(true);
    try {
      await api.patch(`/api/Deal/MarkLost/${lostDeal.id}`, {
        lost_reason: lostForm.lost_reason.trim(),
        lost_notes: lostForm.lost_notes?.trim(),
      });
      success("Deal Closed as Lost", "Stage and history updated");
      setLostDeal(null);
      loadData();
    } catch (err: any) {
      error("Failed", err.message || "Could not update deal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteDeal) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Deal/Delete/${deleteDeal.id}`);
      success("Deal Deleted", "Deal removed from pipeline");
      setDeleteDeal(null);
      loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete deal");
    } finally {
      setActionLoading(false);
    }
  };

  // The API already applied every filter
  const filteredDeals = deals;
  const visibleTotal =
    viewMode === "list" ? listTotal : Object.values(stageMeta).reduce((sum, meta) => sum + meta.total, 0);

  // Calculate Pipeline Metrics
  const totalValue = filteredDeals.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const weightedValue = filteredDeals.reduce((sum, d) => sum + Number(d.weighted_amount || 0), 0);
  const wonCount = filteredDeals.filter((d) => d.deal_status === "WON").length;
  const openCount = filteredDeals.filter((d) => d.deal_status === "OPEN").length;

  // Won and lost columns only belong to the views that look at won or lost deals
  const stages = stagesForView(view, pipeline?.stages || []);
  const maxStageTotal = Math.max(1, ...stages.map((st) => stageMeta[st.id]?.total ?? 0));
  const timeSince = (iso: string) => {
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d >= 1) return `${d} ${d === 1 ? "day" : "days"}`;
    const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
    return h >= 1 ? `${h} h` : "just now";
  };

  const openBulkOwner = () => {
    setBulkOwnerId("");
    setBulkOwnerOpen(true);
    if (!assignable.length) api.get("/api/User/Assignable?for=deal").then((r) => setAssignable(unwrapArray(r))).catch(() => undefined);
  };

  const submitBulkOwner = async () => {
    setActionLoading(true);
    try {
      const r: any = await api.post("/api/Deal/BulkAssign", { deal_ids: Array.from(selected), owner_id: bulkOwnerId });
      const count = Number(r?.count ?? 0);
      const skipped = Number(r?.skipped ?? 0);
      success("Owner changed", `${count} ${count === 1 ? "deal" : "deals"} reassigned${skipped ? `; ${skipped} skipped (already theirs or outside your team)` : ""}`);
      setBulkOwnerOpen(false);
      setSelected(new Set());
      loadDeals();
    } catch (err: any) {
      error("Could not assign", err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const submitBulkStage = async () => {
    setActionLoading(true);
    try {
      const r: any = await api.post("/api/Deal/BulkMoveStage", { deal_ids: Array.from(selected), stage_id: bulkStageId });
      const blocked: { id: string; reason: string }[] = r?.blocked || [];
      if (r?.moved) success("Stage changed", `${r.moved} ${r.moved === 1 ? "deal" : "deals"} moved`);
      if (blocked.length) {
        const names = new Map(filteredDeals.map((d) => [d.id, d.name]));
        error(`${blocked.length} not moved`, blocked.slice(0, 3).map((b) => `${names.get(b.id) || "Deal"}: ${b.reason}`).join(" | "));
      }
      setBulkStageOpen(false);
      setSelected(new Set());
      loadDeals();
    } catch (err: any) {
      error("Could not move", err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  // CSV import: each row becomes a deal at the pipeline's first open stage, with its company and contact
  const firstOpenStage = pipeline?.stages.find((st) => !st.is_won_stage && !st.is_lost_stage);
  const createImportedDeal = async (r: Record<string, string>) => {
    if (!pipeline || !firstOpenStage) throw new Error("This pipeline has no open stage to start deals in");
    await api.post("/api/Deal/Insert", {
      name: r.name,
      company_name: r.company_name,
      contact_name: r.contact_name || undefined,
      contact_phone: r.contact_phone || undefined,
      contact_email: r.contact_email || undefined,
      pipeline_id: pipeline.id,
      stage_id: firstOpenStage.id,
      amount: r.amount ? Number(r.amount.replace(/[,₹\s]/g, "")) : 0,
      expected_close_date: r.expected_close_date ? new Date(r.expected_close_date) : undefined,
    });
  };

  const submitBulkTask = async () => {
    if (!taskForm.title.trim() || !taskForm.due) return;
    setActionLoading(true);
    let made = 0;
    try {
      for (const d of filteredDeals.filter((x) => selected.has(x.id))) {
        await api.post("/api/Task/Insert", {
          title: taskForm.title.trim(),
          task_type: taskForm.type,
          priority: "NORMAL",
          due_at: new Date(taskForm.due).toISOString(),
          assigned_to_id: d.owner_id || user?.Id,
          related_type: "DEAL",
          related_id: d.id,
          related_name: d.name,
        });
        made++;
      }
      success("Tasks added", `${made} task${made === 1 ? "" : "s"} created`);
      setTaskOpen(false);
      setSelected(new Set());
      loadDeals();
    } catch (err: any) {
      error(`Added ${made}, then stopped`, err.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const nextStageOf = (d: DealRecord) => {
    const open = (pipeline?.stages || []).filter((st) => !st.is_won_stage && !st.is_lost_stage);
    const i = open.findIndex((st) => st.id === d.stage_id);
    return i >= 0 && i < open.length - 1 ? open[i + 1] ?? null : null;
  };
  const staleCount = filteredDeals.filter((d) => d.is_stale).length;

  // A saved view, or the filters Ask the CRM hands over with ?apply=
  const applySaved = (c: any) => {
    // "" = every deal, whatever its status (Ask the CRM)
    setView(c.view ?? "open");
    setViewMode(c.viewMode === "list" ? "list" : c.viewMode === "forecast" ? "forecast" : "kanban");
    setPipelineId(c.pipelineId || "");
    setSearch(c.search || "");
    dealFilters.replace(c.filters || {});
    fieldFilters.setAll(c.fieldFilters);
    setSort(c.sort);
  };

  return (
    <div className="space-y-4">
      <ApplyFromUrl apply={applySaved} />
      <ViewsPanel
        title="Deals"
        saved={{
          module: "deals",
          getConfig: () => ({ view, viewMode, pipelineId, search, filters: dealFilters.values, fieldFilters: fieldFilters.values, sort: sort }),
          apply: applySaved,
        }}
        subtitle="Sales pipeline"
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "open", label: "All open", count: viewCounts.open },
              { key: "mine", label: "My deals", count: viewCounts.mine },
              { key: "closing_month", label: "Closing this month", count: viewCounts.closing_month },
              { key: "stale", label: "Stale 14d+", count: viewCounts.stale, tone: "warning" },
              { key: "won_quarter", label: "Won this quarter", count: viewCounts.won_quarter },
              { key: "lost", label: "Lost", count: viewCounts.lost },
              ...(healthOn || view === "at_risk" ? [{ key: "at_risk", label: "At risk (AI)", count: viewCounts.at_risk, tone: "danger" as const }] : []),
            ],
          },
        ]}
        footer={
          pipelines.length > 0 ? (
            <div className="mt-3 flex flex-col gap-0.5">
              <div className="px-2.5 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Pipelines</div>
              {pipelines.map((pl) => {
                const on = (pipelineId || pipelines.find((x) => x.is_default)?.id || pipelines[0]?.id) === pl.id;
                return (
                  <button
                    key={pl.id}
                    type="button"
                    onClick={() => setPipelineId(pl.is_default ? "" : pl.id)}
                    className={`flex h-[30px] items-center justify-between rounded-md px-2.5 text-left text-[13px] ${on ? "font-medium text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                  >
                    <span className="truncate">{pl.name}</span>
                    {on && <span className="size-1.5 rounded-full bg-primary" />}
                  </button>
                );
              })}
            </div>
          ) : undefined
        }
      />

      <PageHeader
        title={{ open: "All open deals", mine: "My deals", closing_month: "Closing this month", stale: "Stale deals", won_quarter: "Won this quarter", lost: "Lost deals", at_risk: "Deals the AI sees at risk" }[view] || "Deals"}
        summary={visibleTotal || filteredDeals.length}
        switcher={<ViewSwitch label="Deal layout" value={viewMode} onChange={setViewMode} options={[{ key: "kanban", label: "Board" }, { key: "list", label: "Table" }, { key: "forecast", label: "Forecast" }]} />}
        actions={
          <>
            {hasPermission("deal.create") && (
              <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => setImportOpen(true)}>
                Import
              </Button>
            )}
            <Button size="sm" data-new-record onClick={() => openAdd()}>
              <Plus className="size-4 mr-1.5" />
              Deal
            </Button>
          </>
        }
      />

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search deals, accounts..." }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(dealOptions.owners, dealOptions.total, user?.Id), defaultValue: "all" },
          {
            name: "stage",
            label: "Stage",
            icon: FilterStageIcon,
            type: "select",
            options: [{ value: "all", label: "All stages" }, ...(pipeline?.stages || []).map((st) => ({ value: st.id, label: st.name }))],
            defaultValue: "all",
          },
          { name: "close", label: "Expected close", icon: FilterCloseIcon, type: "date", dates: "future", defaultValue: "any" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={dealFilters.values}
        onChange={(name, value) => dealFilters.set(name as keyof typeof DEAL_FILTER_DEFAULTS, value)}
        onReset={() => {
          dealFilters.reset();
          setView("open");
        }}
        quickKey="deals"
        fieldFilters={{ fields: dealFields || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/Deal/FilterValues" }}
        sort={viewMode !== "forecast" ? { fields: dealFields || [], value: sort, onChange: setSort, defaultLabel: "Manual" } : undefined}
        shown={visibleTotal || filteredDeals.length}
        total={dealOptions.total || undefined}
        noun="deals"
        columns={viewMode === "list" ? { options: DEAL_COLUMNS, value: columns.visible, onChange: columns.setVisible } : undefined}
      />

      {/* Main View Area */}
      {viewMode === "forecast" ? (
        <DealsForecast
          query={(() => {
            const f = dealFilters.values;
            return toQuery({ view, sort: "close", pipeline_id: pipelineId || undefined, search: search.trim(), owner: f.owner, stage_id: f.stage, ...closeParams(f.close), ...dateRangeParams(f.created, "created"), f: fieldFilters.param(dealFields) });
          })()}
          refreshKey={deals.length}
          onError={(m) => error("Fetch Error", m)}
        />
      ) : loading ? (
        <TableSkeleton />
      ) : viewMode === "kanban" ? (
        /* Kanban Board View */
        <div className="space-y-3">
          {/* Totals and scroll controls */}
          <div className="flex items-center justify-between gap-2 px-1 text-xs">
            <span className="text-muted-foreground">
              Total <strong className="font-mono text-foreground">{formatINRCompact(totalValue)}</strong> · Weighted <strong className="font-mono text-foreground">{formatINRCompact(weightedValue)}</strong>
              {staleCount > 0 && <span className="text-warning"> · {staleCount} stale</span>}
            </span>
            <div className="flex items-center gap-1.5">
              <Button type="button" variant="outline" size="icon-sm" onClick={() => scrollKanban("left")} title="Scroll left" aria-label="Scroll left">
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button type="button" variant="outline" size="icon-sm" onClick={() => scrollKanban("right")} title="Scroll right" aria-label="Scroll right">
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>

          {/* Board with the selected deal docked on the right */}
          <div className="flex items-start gap-0">
            <div className="min-w-0 flex-1">
          {/* Scrollable Columns Container */}
          <div
            ref={kanbanRef}
            className="flex gap-2.5 overflow-x-auto pb-4 pt-1 items-start"
          >
            {stages.map((stg) => {
              const stageDeals = filteredDeals.filter((d) => d.stage_id === stg.id);
              const stageSum = stageDeals.reduce((sum, d) => sum + Number(d.amount || 0), 0);
              const isOver = dragOverStageId === stg.id;
              const isCurrentStageDrop = dropSlot?.stageId === stg.id;
              const currentDropIndex = isCurrentStageDrop ? dropSlot.index : -1;

              return (
                <div
                  key={stg.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverStageId !== stg.id) setDragOverStageId(stg.id);
                    if (draggedDealId && (!dropSlot || dropSlot.stageId !== stg.id)) {
                      setDropSlot({ stageId: stg.id, index: stageDeals.length });
                    }
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setDragOverStageId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverStageId(null);
                    const sourceId = e.dataTransfer.getData("text/plain") || draggedDealId;
                    const targetIdx = dropSlot?.stageId === stg.id ? dropSlot.index : stageDeals.length;
                    setDropSlot(null);
                    setDraggedDealId(null);
                    if (sourceId) {
                      handleDropAtSlot(sourceId, stg, targetIdx);
                    }
                  }}
                  className={`w-[272px] shrink-0 rounded-xl bg-muted p-2 flex flex-col h-[calc(100vh-290px)] min-h-[420px] ${
                    isOver && !dropSlot ? "ring-2 ring-primary/50 bg-primary/5" : ""
                  }`}
                >
                  {/* Stage header: name, count, probability, total and share of the pipeline */}
                  <div className="mb-1.5 flex flex-col gap-1 px-1.5 pt-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-semibold">
                        {stg.name} <span className="ml-0.5 font-mono font-normal text-muted-foreground">{stageMeta[stg.id]?.total ?? stageDeals.length}</span>
                      </span>
                      <span className="text-[11px] text-muted-foreground">{stg.probability_percent}%</span>
                    </div>
                    <span className="font-mono text-[12.5px]">{formatINRCompact(stageSum)}</span>
                    <div className="h-[3px] w-full bg-border">
                      <div className="h-full" style={{ width: `${Math.round(((stageMeta[stg.id]?.total ?? 0) / maxStageTotal) * 100)}%`, backgroundColor: stg.color_code || "var(--primary)" }} />
                    </div>
                  </div>

                  {/* Cards Container */}
                  <div
                    className="space-y-1.5 overflow-y-auto flex-1 min-h-0 flex flex-col"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (draggedDealId && (!dropSlot || dropSlot.stageId !== stg.id)) {
                        setDropSlot({ stageId: stg.id, index: stageDeals.length });
                      }
                    }}
                  >
                    {stageDeals.length === 0 ? (
                      <div
                        className={`flex-1 min-h-[220px] flex items-center justify-center p-6 text-center border-2 border-dashed rounded-xl text-xs transition-colors ${
                          isOver || (isCurrentStageDrop && draggedDealId)
                            ? "border-primary bg-primary/10 text-primary font-semibold"
                            : "text-muted-foreground/60 border-muted/80"
                        }`}
                      >
                        {isOver || (isCurrentStageDrop && draggedDealId) ? "Drop deal here" : "No deals in stage"}
                      </div>
                    ) : (
                      stageDeals.map((deal, idx) => {
                        const isWon = deal.deal_status === "WON";
                        const isLost = deal.deal_status === "LOST";
                        const isDragging = draggedDealId === deal.id;
                        const isSpaceBefore =
                          isCurrentStageDrop && currentDropIndex === idx && draggedDealId !== deal.id;

                        return (
                          <div
                            key={deal.id}
                            className="relative transition-all duration-150"
                            style={{
                              paddingTop: isSpaceBefore ? "108px" : "0px",
                            }}
                          >
                            {/* Visual Drop Slot opening above this card */}
                            {isSpaceBefore && (
                              <div className="absolute top-0 left-0 right-0 h-24 rounded-xl border-2 border-dashed border-primary bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary select-none pointer-events-none shadow-xs">
                                <div className="flex items-center gap-1.5">
                                  <Move className="size-3.5" />
                                  <span>Drop deal here</span>
                                </div>
                              </div>
                            )}

                            <div
                              draggable={true}
                              onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", deal.id);
                                e.dataTransfer.effectAllowed = "move";
                                isDraggingRef.current = true;
                                setDraggedDealId(deal.id);
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = "move";
                                if (!draggedDealId) return;
                                const rect = e.currentTarget.getBoundingClientRect();
                                const midY = rect.top + rect.height / 2;
                                const targetIdx = e.clientY < midY ? idx : idx + 1;
                                if (!dropSlot || dropSlot.stageId !== stg.id || dropSlot.index !== targetIdx) {
                                  setDropSlot({ stageId: stg.id, index: targetIdx });
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const sourceId = e.dataTransfer.getData("text/plain") || draggedDealId;
                                const targetIdx = dropSlot?.stageId === stg.id ? dropSlot.index : idx;
                                if (sourceId) {
                                  handleDropAtSlot(sourceId, stg, targetIdx);
                                }
                              }}
                              onDragEnd={() => {
                                isDraggingRef.current = false;
                                autoScrollSpeedRef.current = 0;
                                setDropSlot(null);
                                setDraggedDealId(null);
                                setDragOverStageId(null);
                              }}
                              onClick={() => {
                                if (isDraggingRef.current) return;
                                setQuickDeal(deal);
                              }}
                              className={`group group/card relative flex cursor-grab select-none flex-col gap-1.5 rounded-lg bg-card p-2.5 shadow-[0_1px_2px_rgba(16,21,58,0.08)] transition-shadow hover:shadow-md active:cursor-grabbing ${
                                quickDeal?.id === deal.id ? "ring-2 ring-primary" : ""
                              } ${isDragging ? "opacity-30 ring-1 ring-dashed ring-primary" : ""}`}
                            >
                              <div className="flex items-start justify-between gap-1.5">
                                <span className="text-[13px] font-semibold leading-snug">{deal.name}</span>
                                <span className="mt-1 flex shrink-0 items-center gap-1">
                                  {!isWon && !isLost && <HealthDot health={deal.health} />}
                                  {deal.buying_signal && <span title="Buying signal: proposal opened today" className="size-2 rounded-full bg-gold" />}
                                </span>
                              </div>
                              <span className="truncate text-xs text-muted-foreground">{deal.account?.name || "Independent account"}</span>
                              <div className="flex items-center justify-between gap-1.5">
                                <span className="font-mono text-[13px] font-semibold">{formatINRCompact(deal.amount)}</span>
                                <span className="flex items-center gap-1.5">
                                  {isWon ? (
                                    <span className="rounded bg-gold-soft px-1.5 font-mono text-[11px] leading-[18px] text-gold-ink">Won</span>
                                  ) : isLost ? (
                                    <span className="rounded bg-danger-soft px-1.5 font-mono text-[11px] leading-[18px] text-danger">Lost</span>
                                  ) : (
                                    deal.days_in_stage !== undefined && (
                                      <span
                                        title={deal.over_time_limit ? `Over the time limit for this stage (${deal.stage?.sla_days} days)` : "Days in this stage"}
                                        className={`rounded px-1.5 font-mono text-[11px] leading-[18px] ${deal.is_stale || deal.over_time_limit ? "bg-warning-soft text-warning" : "bg-muted text-foreground"}`}
                                      >
                                        {deal.days_in_stage}d
                                      </span>
                                    )
                                  )}
                                  <PersonDot name={deal.owner ? `${deal.owner.first_name} ${deal.owner.last_name || ""}`.trim() : ""} />
                                </span>
                              </div>
                              {canEditInline && deal.deal_status === "OPEN" && (
                                <MoveSelect
                                  label={`Move ${deal.name} to another stage`}
                                  options={stages.filter((s) => s.id !== deal.stage_id).map((s) => ({ value: s.id, label: s.name }))}
                                  onPick={(id) => {
                                    const to = stages.find((s) => s.id === id);
                                    if (to) void handleDropAtSlot(deal.id, to, 0);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* If dropSlot is after all cards */}
                    {isCurrentStageDrop &&
                      currentDropIndex >= stageDeals.length &&
                      draggedDealId && (
                        <div className="h-24 shrink-0 rounded-xl border-2 border-dashed border-primary bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary select-none pointer-events-none shadow-xs">
                          <div className="flex items-center gap-1.5">
                            <Move className="size-3.5" />
                            <span>Drop deal here</span>
                          </div>
                        </div>
                      )}
                    <InfiniteScroll
                      hasMore={(() => {
                        const meta = stageMeta[stg.id];
                        return !!meta && meta.page * meta.pageSize < meta.total;
                      })()}
                      loading={!!stageMeta[stg.id]?.loading}
                      onLoad={() => loadMoreStage(stg.id)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
            </div>
            {quickDeal && (
              <DealQuickView
                deal={quickDeal}
                nextStage={nextStageOf(quickDeal)}
                onClose={() => setQuickDeal(null)}
                onMoveNext={(d, st) => handleDropAtSlot(d.id, st, 0)}
                onDelete={setDeleteDeal}
              />
            )}
          </div>
        </div>
      ) : (
        /* Table list view */
        <div className="space-y-3">
          <BulkBar count={selected.size} onClear={() => setSelected(new Set())}>
            <BulkButton onClick={openBulkOwner}>Assign owner</BulkButton>
            {canEditInline && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
            <BulkButton onClick={() => { setBulkStageId(""); setBulkStageOpen(true); }}>Change stage</BulkButton>
            {hasPermission("activity.create") && (
              <BulkButton
                onClick={() => {
                  setTaskForm({ title: "", due: new Date(Date.now() + 86400000).toISOString().slice(0, 16), type: "CALL" });
                  setTaskOpen(true);
                }}
              >
                Add task
              </BulkButton>
            )}
          </BulkBar>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <Table className="min-w-[960px]">
              <TableHeader>
                <TableRow className="bg-panel text-[11px] hover:bg-panel">
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={filteredDeals.length > 0 && selected.size === filteredDeals.length}
                      onChange={() => setSelected(selected.size === filteredDeals.length ? new Set() : new Set(filteredDeals.map((d) => d.id)))}
                      className="size-4 cursor-pointer accent-primary"
                    />
                  </TableHead>
                  <TableHead>Deal</TableHead>
                  <TableHead className={colHidden("stage")}>Stage</TableHead>
                  <TableHead className={`text-right ${colHidden("amount")}`}>
                    <SortHead label="Amount" col="amount" sort={sort} onSort={setSort} />
                  </TableHead>
                  <TableHead className={`text-right ${colHidden("prob")}`}>Prob.</TableHead>
                  <TableHead className={colHidden("close")}>
                    <SortHead label="Close" col="expected_close" sort={sort} onSort={setSort} />
                  </TableHead>
                  <TableHead className={colHidden("owner")}>Owner</TableHead>
                  <TableHead className={colHidden("last")}>Last activity</TableHead>
                  <TableHead className={colHidden("next")}>Next step</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="p-0">
                      <ListEmpty
                        noun="deals"
                        icon={Kanban}
                        filtered={!!search.trim() || view !== "open" || dealFilters.activeCount > 0 || fieldFilters.activeCount > 0}
                        onClear={() => {
                          setSearch("");
                          setView("open");
                          dealFilters.reset();
                          fieldFilters.reset();
                        }}
                        createLabel="+ New deal"
                        onCreate={hasPermission("deal.create") ? () => openAdd() : undefined}
                        firstUse="Deals are created when you convert a qualified lead, or you can add one directly."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDeals.map((d) => {
                    const overdue = d.deal_status === "OPEN" && !!d.expected_close_date && new Date(d.expected_close_date) < new Date(new Date().setHours(0, 0, 0, 0));
                    const last = d.last_activity;
                    return (
                      <TableRow key={d.id} className={`group/row text-[13px] ${selected.has(d.id) ? "bg-accent/40" : ""}`}>
                        <TableCell>
                          <input
                            type="checkbox"
                            aria-label="Select deal"
                            checked={selected.has(d.id)}
                            onChange={() =>
                              setSelected((cur) => {
                                const next = new Set(cur);
                                if (next.has(d.id)) next.delete(d.id);
                                else next.add(d.id);
                                return next;
                              })
                            }
                            className="size-4 cursor-pointer accent-primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Link to={`/deals/${d.id}`} className="font-semibold text-foreground hover:text-primary hover:underline">
                            {d.name}
                          </Link>
                          <PreviewButton entity="Deal" id={d.id} label={d.name} className="ml-1 align-middle" />
                          {d.deal_status === "OPEN" && <HealthDot health={d.health} className="ml-1.5 inline-block align-middle" />}
                          <div className="text-xs text-muted-foreground">{[d.deal_number, d.account?.name || "—"].filter(Boolean).join(" · ")}</div>
                        </TableCell>
                        <TableCell className={colHidden("stage")}>
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-xs">
                            <span className="size-1.5 rounded-full" style={{ backgroundColor: d.stage?.color_code || "var(--primary)" }} />
                            {d.stage?.name || "Unknown"}
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${colHidden("amount")}`}>
                          <InlineCell
                            list="Deal" id={d.id} field="amount" kind="number" value={d.amount}
                            enabled={canEditInline && d.deal_status === "OPEN"} className="justify-end"
                            display={formatINRCompact(d.amount)}
                            onSaved={(row) => setDeals((ds) => ds.map((x) => (x.id === d.id ? { ...x, ...row } as any : x)))} onConflict={() => loadDeals()}
                          />
                        </TableCell>
                        <TableCell className={`text-right font-mono ${colHidden("prob")}`}>{d.probability}%</TableCell>
                        <TableCell className={`whitespace-nowrap ${overdue ? "font-semibold text-danger" : ""} ${colHidden("close")}`}>
                          <InlineCell
                            list="Deal" id={d.id} field="expected_close_date" kind="date" value={d.expected_close_date}
                            enabled={canEditInline && d.deal_status === "OPEN"}
                            display={<>{d.expected_close_date ? new Date(d.expected_close_date).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }) : "—"}{overdue && " · overdue"}</>}
                            onSaved={(row) => setDeals((ds) => ds.map((x) => (x.id === d.id ? { ...x, ...row } as any : x)))} onConflict={() => loadDeals()}
                          />
                        </TableCell>
                        <TableCell className={colHidden("owner")}>
                          <OwnerCell list="Deal" id={d.id} ownerId={d.owner_id} owner={d.owner} enabled={canEditInline && d.deal_status === "OPEN"} onSaved={(row) => setDeals((cur) => cur.map((x) => (x.id === d.id ? { ...x, ...row } : x)))} onConflict={() => loadDeals()} />
                        </TableCell>
                        <TableCell className={`whitespace-nowrap text-xs ${d.is_stale ? "text-warning" : "text-muted-foreground"} ${colHidden("last")}`}>
                          {last ? `${last.type.charAt(0) + last.type.slice(1).toLowerCase()} · ${timeSince(last.at)}` : "—"}
                        </TableCell>
                        <TableCell className={`text-xs ${d.next_step || d.deal_status !== "OPEN" ? "" : "text-warning"} ${colHidden("next")}`}>
                          {d.next_step ? d.next_step.title : d.deal_status === "OPEN" ? "No next step" : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <ListPager page={listPage} pageSize={listPageSize} total={listTotal} onPage={setListPage} />
          </div>
        </div>
      )}

      {/* Import deals from a CSV, with a preview */}
      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import deals"
        noun="deals"
        fields={DEAL_IMPORT_FIELDS}
        create={createImportedDeal}
        onDone={() => loadData()}
        hint="Each deal starts at the first open stage of this pipeline, with its company and contact."
      />

      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="Deal" ids={Array.from(selected)} fields={DEAL_BULK_FIELDS} onDone={() => loadDeals()} />

      {/* Bulk: a task on each selected deal */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add a task</DialogTitle>
            <DialogDescription>One task is created on each of the {selected.size} selected deals, for the deal's owner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="What needs doing, e.g. Confirm the quote" value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} />
            <div className="flex gap-2">
              <SearchableSelect value={taskForm.type} onChange={(e) => setTaskForm((f) => ({ ...f, type: e.target.value }))} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                <option value="CALL">Call</option>
                <option value="EMAIL">Email</option>
                <option value="MEETING">Meeting</option>
                <option value="FOLLOW_UP">Follow-up</option>
              </SearchableSelect>
              <Input type="datetime-local" value={taskForm.due} onChange={(e) => setTaskForm((f) => ({ ...f, due: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskOpen(false)}>Cancel</Button>
            <Button disabled={actionLoading || !taskForm.title.trim() || !taskForm.due} onClick={submitBulkTask}>Create tasks</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk: assign owner */}
      <Dialog open={bulkOwnerOpen} onOpenChange={setBulkOwnerOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign owner</DialogTitle>
            <DialogDescription>Give the {selected.size} selected {selected.size === 1 ? "deal" : "deals"} to one person.</DialogDescription>
          </DialogHeader>
          <SearchableSelect value={bulkOwnerId} onChange={(e) => setBulkOwnerId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Choose a person</option>
            {assignable.map((u) => (
              <option key={u.id} value={u.id}>{`${u.first_name} ${u.last_name || ""}`.trim()}</option>
            ))}
          </SearchableSelect>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOwnerOpen(false)}>Cancel</Button>
            <Button disabled={!bulkOwnerId || actionLoading} onClick={submitBulkOwner}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk: change stage (every deal still goes through its stage gates) */}
      <Dialog open={bulkStageOpen} onOpenChange={setBulkStageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change stage</DialogTitle>
            <DialogDescription>Deals that are not ready for the stage stay where they are, and are listed afterwards.</DialogDescription>
          </DialogHeader>
          <SearchableSelect value={bulkStageId} onChange={(e) => setBulkStageId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Choose a stage</option>
            {(pipeline?.stages || []).filter((st) => !st.is_won_stage && !st.is_lost_stage).map((st) => (
              <option key={st.id} value={st.id}>{st.name}</option>
            ))}
          </SearchableSelect>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStageOpen(false)}>Cancel</Button>
            <Button disabled={!bulkStageId || actionLoading} onClick={submitBulkStage}>Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Deal / Opportunity Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl">
          <form onSubmit={handleAddSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="size-5 text-primary" />
                Add Opportunity to Pipeline
              </DialogTitle>
              <DialogDescription>
                Add a new prospect or project opportunity directly into your active sales pipeline.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-3 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="deal_name">Opportunity / Deal Name *</Label>
                <Input
                  id="deal_name"
                  placeholder="e.g. 5,000 Metres Fabric Supply or CCTV Installation"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              {/* Company / Account Selection (Toggle Existing vs New) */}
              <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs sm:text-sm font-semibold">Client Company / Account *</Label>
                  <div className="flex rounded-md bg-muted p-0.5 border">
                    <button
                      type="button"
                      onClick={() => setAccountMode("existing")}
                      className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                        accountMode === "existing"
                          ? "bg-background text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Existing
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccountMode("new")}
                      className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                        accountMode === "new"
                          ? "bg-background text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      + New Company
                    </button>
                  </div>
                </div>

                {accountMode === "existing" ? (
                  <SearchSelect
                    endpoint="/api/Account/List"
                    value={form.account_id}
                    selectedLabel={accountLabel}
                    getLabel={(row) => row.name}
                    placeholder="Search accounts"
                    allowEmpty
                    emptyLabel="Select an existing account"
                    onChange={(id, row) => {
                      // A contact chosen for another account no longer fits
                      if (id !== form.account_id) setPresetContact(null);
                      setForm((p) => ({ ...p, account_id: id }));
                      setAccountLabel(String(row?.name || ""));
                    }}
                  />
                ) : (
                  <Input
                    id="company_name"
                    placeholder="Enter company or client organization name (e.g. Acme Tech)"
                    value={form.company_name}
                    onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
                    required={accountMode === "new"}
                  />
                )}
              </div>

              {/* Primary Contact Details */}
              {presetContact && accountMode === "existing" ? (
                <div className="rounded-lg border bg-muted/30 p-3 flex items-center justify-between gap-3 text-sm">
                  <span>
                    <span className="text-xs font-semibold text-muted-foreground block">Primary contact</span>
                    {presetContact.name}
                  </span>
                  <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setPresetContact(null)}>
                    Use someone else
                  </Button>
                </div>
              ) : (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <Label className="text-xs sm:text-sm font-semibold">Primary Contact Details (Optional)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                  <Input
                    placeholder="Contact Name"
                    value={form.contact_name}
                    onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
                  />
                  <Input
                    placeholder="Phone / Mobile"
                    value={form.contact_phone}
                    onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))}
                  />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={form.contact_email}
                    onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
                  />
                </div>
              </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="amount">Expected Value (₹) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: Number(e.target.value) }))}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="stage_id">Pipeline Stage *</Label>
                  <SearchableSelect
                    id="stage_id"
                    className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 sm:px-3.5 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                    value={form.stage_id}
                    onChange={(e) => setForm((p) => ({ ...p, stage_id: e.target.value }))}
                  >
                    {stages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.probability_percent}%)
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="expected_close_date">Expected Close Date</Label>
                <Input
                  id="expected_close_date"
                  type="date"
                  value={form.expected_close_date}
                  onChange={(e) => setForm((p) => ({ ...p, expected_close_date: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Saving..." : "Add to Pipeline"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Move Stage Dialog */}
      <Dialog open={!!moveDeal} onOpenChange={(open) => !open && setMoveDeal(null)}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleMoveSubmit}>
            <DialogHeader>
              <DialogTitle>Advance Pipeline Stage</DialogTitle>
              <DialogDescription>
                Move <strong>{moveDeal?.name}</strong> to a different pipeline stage.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="move_stage_select">Select Target Stage</Label>
                <SearchableSelect
                  id="move_stage_select"
                  className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 sm:px-3.5 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                  value={moveStageId}
                  onChange={(e) => setMoveStageId(e.target.value)}
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.probability_percent}%)
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMoveDeal(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Moving..." : "Confirm Move"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StageMoveDialog request={moveRequest} onClose={() => setMoveRequest(null)} onMoved={() => loadData()} />

      {/* Mark Won Dialog */}
      <Dialog open={!!wonDeal} onOpenChange={(open) => !open && setWonDeal(null)}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleWonSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-success">
                <CheckCircle className="size-5" />
                Close Deal as Won
              </DialogTitle>
              <DialogDescription>
                Record final won revenue and close date for <strong>{wonDeal?.name}</strong>.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="won_amount">Final Closed Amount (₹) *</Label>
                <Input
                  id="won_amount"
                  type="number"
                  min="1"
                  value={wonForm.won_amount}
                  onChange={(e) => setWonForm((p) => ({ ...p, won_amount: Number(e.target.value) }))}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="actual_close_date">Actual Close Date *</Label>
                <Input
                  id="actual_close_date"
                  type="date"
                  value={wonForm.actual_close_date}
                  onChange={(e) => setWonForm((p) => ({ ...p, actual_close_date: e.target.value }))}
                  required
                />
              </div>

              {wonGate && !wonGate.allowed && (
                <div className="rounded-md border border-warning/30 bg-warning/10 p-3 space-y-2">
                  <p className="text-xs font-semibold text-warning">Not ready to close as Won:</p>
                  <ul className="list-disc pl-4 text-xs text-warning space-y-0.5">
                    {wonGate.missing.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                  {wonGate.can_override ? (
                    <div className="space-y-1">
                      <Label htmlFor="won_reason" className="text-xs">Why close it as Won anyway? (saved in the history and security log)</Label>
                      <Input id="won_reason" value={wonReason} onChange={(e) => setWonReason(e.target.value)} placeholder="e.g. Repeat order, paid upfront" />
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Upload the signed agreement or PO on the deal page first, or ask your Sales Head.</p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWonDeal(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-success hover:bg-success/90 text-white"
                disabled={actionLoading || (!!wonGate && !wonGate.allowed && (!wonGate.can_override || !wonReason.trim()))}
              >
                {actionLoading ? "Closing..." : "Mark as Won"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Lost Dialog */}
      <Dialog open={!!lostDeal} onOpenChange={(open) => !open && setLostDeal(null)}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={handleLostSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <XCircle className="size-5" />
                Close Deal as Lost
              </DialogTitle>
              <DialogDescription>
                Lost deals require a mandatory reason for sales analytics.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="lost_reason" className="text-destructive font-semibold">
                  Lost Reason *
                </Label>
                <SearchableSelect
                  id="lost_reason"
                  className="w-full h-9 sm:h-10 rounded-md border border-input bg-background px-3 sm:px-3.5 text-xs sm:text-sm focus:ring-1 focus:ring-ring"
                  value={lostForm.lost_reason}
                  onChange={(e) => setLostForm((p) => ({ ...p, lost_reason: e.target.value }))}
                  required
                >
                  <option value="">-- Select lost reason --</option>
                  {lostReasons.map((r) => (
                    <option key={r.code} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lost_notes">Additional Lost Notes</Label>
                <Input
                  id="lost_notes"
                  placeholder="Lessons learned or client feedback"
                  value={lostForm.lost_notes}
                  onChange={(e) => setLostForm((p) => ({ ...p, lost_notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLostDeal(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={actionLoading}>
                {actionLoading ? "Closing..." : "Confirm Lost"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Deal Dialog */}
      <Dialog open={!!deleteDeal} onOpenChange={(open) => !open && setDeleteDeal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete Deal</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteDeal?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDeal(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSubmit} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
